import { useCallback } from 'react';
import { ActeurMinimalDto } from 'api/funnel';
import { funnelActorsAPI } from 'services/swagger-api';
import { useAsyncResource } from 'back-office/shared/hooks/useAsyncResource';

export interface ActorOption {
  /** personId : identifiant attendu par les commandes du back. */
  key: string;
  label: string;
  /** Nom de l'acteur seul, sans son plateforme ni son rôle. */
  name: string;
  role: string;
}

const MAX_RESULT_COUNT = 500;

type ActorRole =
  | 'assureur'
  | 'association'
  | 'grossiste'
  | 'gestionnaireContrat'
  | 'gestionnaireSinistre'
  | 'gestionnaireCompta';

const ROLE_LOADERS: Record<ActorRole, { label: string; load: () => Promise<{ items?: ActeurMinimalDto[] }> }> = {
  assureur: {
    label: 'Assureur',
    load: () => funnelActorsAPI.getInsurerActors({ maxResultCount: MAX_RESULT_COUNT }),
  },
  association: {
    label: 'Association',
    load: () => funnelActorsAPI.getAssociationActors({ maxResultCount: MAX_RESULT_COUNT }),
  },
  grossiste: {
    label: 'Grossiste',
    load: () => funnelActorsAPI.getWholesalerActors({ maxResultCount: MAX_RESULT_COUNT }),
  },
  gestionnaireContrat: {
    label: 'Gestionnaire contrat',
    load: () => funnelActorsAPI.getContractManagerActors({ maxResultCount: MAX_RESULT_COUNT }),
  },
  gestionnaireSinistre: {
    label: 'Gestionnaire sinistre',
    load: () => funnelActorsAPI.getClaimManagerActors({ maxResultCount: MAX_RESULT_COUNT }),
  },
  gestionnaireCompta: {
    label: 'Gestionnaire comptabilité',
    load: () => funnelActorsAPI.getAccountingManagerActors({ maxResultCount: MAX_RESULT_COUNT }),
  },
};

const ALL_ROLES = Object.keys(ROLE_LOADERS) as ActorRole[];

/**
 * Rôles éligibles à une commission selon le code de sa nature : une commission
 * gestionnaire revient aux comptes porteurs du rôle correspondant, quelle que soit la
 * nature de leur personne morale. Les autres commissions restent sur le cas par défaut.
 */
const ROLES_BY_NATURE_CODE: Record<string, ActorRole[]> = {
  CGC: ['gestionnaireContrat'],
  CGS: ['gestionnaireSinistre'],
  CGCPT: ['gestionnaireCompta'],
};

/** Cas par défaut de l'ancien back-office : seul un assureur peut encaisser. */
const DEFAULT_ROLES: ActorRole[] = ['assureur'];

export const rolesForNature = (natureCode?: string | null): ActorRole[] =>
  (natureCode ? ROLES_BY_NATURE_CODE[natureCode.trim().toUpperCase()] : undefined) ?? DEFAULT_ROLES;

/**
 * Fusionne les options d'une même personne (une option par rôle à l'origine) : le
 * libellé est celui de la première option, les rôles sont concaténés.
 */
export const mergeByPerson = (options: ActorOption[]): ActorOption[] => {
  const byPerson = new Map<string, ActorOption>();

  for (const option of options) {
    const existing = byPerson.get(option.key);
    if (!existing) {
      byPerson.set(option.key, { ...option });
    } else if (!existing.role.split(', ').includes(option.role)) {
      existing.role = `${existing.role}, ${option.role}`;
    }
  }

  return Array.from(byPerson.values()).sort((a, b) => a.label.localeCompare(b.label, 'fr'));
};

/**
 * Bénéficiaires possibles d'un frais : toutes les personnes portant un rôle (assureur,
 * grossiste, association, gestionnaires contrat / sinistre / comptabilité), une seule
 * fois par personne avec ses rôles affichés. Aucun filtrage par nature : les natures du
 * référentiel (« Frais distributeur », « Frais grossiste »…) ne disent pas qui encaisse,
 * et le filtrage par libellé écartait Confiancia, porteur du rôle gestionnaire contrat.
 */
export const useFeeBeneficiaryOptions = () => {
  const fetchActors = useCallback(async () => mergeByPerson(await loadRoles(ALL_ROLES)), []);
  return useAsyncResource(fetchActors);
};

/**
 * Bénéficiaires possibles d'une commission, selon le code de sa nature. Si le filtrage
 * ne renvoie personne, tous les rôles sont proposés plutôt qu'une liste vide.
 */
export const useCommissionBeneficiaryOptions = (natureCode?: string | null) => {
  const fetchActors = useCallback(async (): Promise<ActorOption[]> => {
    const options = await loadRoles(rolesForNature(natureCode));

    if (options.length > 0) return options;

    console.warn(
      `Aucun bénéficiaire éligible pour la nature « ${natureCode ?? '—'} » : tous les rôles sont proposés.`
    );
    return loadRoles(ALL_ROLES);
  }, [natureCode]);

  return useAsyncResource(fetchActors);
};

/**
 * Bénéficiaires possibles d'un tunnel : celui qui encaisse la prime est l'assureur ou le
 * grossiste qui porte le produit. Contrairement aux frais, aucune nature ne vient
 * restreindre la liste — il n'y a rien à filtrer à la création.
 *
 * Les listes d'acteurs sont des utilisateurs par rôle : un même compte à la fois assureur
 * et grossiste (Actudata, Confiancia…) ressortait donc deux fois, à l'identique. Comme le
 * choix porte sur la personne et non sur son rôle, les doublons sont fusionnés sur le
 * personId — le même identifiant est envoyé dans les deux cas — et seul le nom est
 * affiché : le rôle n'a rien à trancher ici, et la plateforme vaut « Aucune
 * plateforme » pour la quasi-totalité d'entre eux.
 */
export const useTunnelBeneficiaryOptions = () => {
  const fetchActors = useCallback(async (): Promise<ActorOption[]> => {
    const options = await loadRoles(['assureur', 'grossiste']);
    const byPerson = new Map<string, ActorOption>();

    for (const option of options) {
      if (!byPerson.has(option.key)) {
        byPerson.set(option.key, { ...option, label: option.name });
      }
    }

    return Array.from(byPerson.values()).sort((a, b) => a.label.localeCompare(b.label, 'fr'));
  }, []);

  return useAsyncResource(fetchActors);
};

/** Assureurs seuls, pour les blocs assureur / co-assureur / ré-assureur d'une garantie. */
export const useInsurerOptions = () => {
  const fetchInsurers = useCallback(() => loadRoles(['assureur']), []);
  return useAsyncResource(fetchInsurers);
};

/**
 * Compagnies porteurs de risque (PM de nature CIE) pour les blocs assureur /
 * co-assureur / ré-assureur d'une garantie : le porteur est la personne morale
 * elle-même, plus un compte utilisateur — la clé est le PersonneMoraleId attendu
 * par assureurPersonneMoraleId et ses variantes co/ré.
 */
export const useCompagnieOptions = () => {
  const fetchCompagnies = useCallback(async (): Promise<ActorOption[]> => {
    const response = await funnelActorsAPI.getCompagnies({ maxResultCount: MAX_RESULT_COUNT });
    return (response.items ?? [])
      .filter((item): item is typeof item & { id: string } => Boolean(item.id))
      .map((item) => ({
        key: item.id,
        label: item.sousNatureLibelle
          ? `${item.designation} — ${item.sousNatureLibelle}`
          : item.designation ?? '',
        name: item.designation ?? '',
        role: 'Compagnie',
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'fr'));
  }, []);
  return useAsyncResource(fetchCompagnies);
};

const loadRoles = async (roles: ActorRole[]): Promise<ActorOption[]> => {
  const responses = await Promise.all(roles.map((role) => ROLE_LOADERS[role].load()));

  return responses
    .flatMap((response, index) => toOptions(response.items, ROLE_LOADERS[roles[index]].label))
    .sort((a, b) => a.label.localeCompare(b.label, 'fr'));
};

/** Un acteur sans personId ne peut pas être référencé par les commandes : on l'écarte. */
const toOptions = (items: ActeurMinimalDto[] | undefined, role: string): ActorOption[] =>
  (items ?? [])
    .filter((item): item is ActeurMinimalDto & { personId: string } => Boolean(item.personId))
    .map((item) => ({
      key: item.personId,
      label: item.plateformeName ? `${item.label} — ${item.plateformeName}` : item.label ?? '',
      name: item.label ?? '',
      role,
    }));
